// 🔗 Polling compartilhado entre as abas do mesmo navegador
//
// Problema: cada aba aberta do Escalone rodava os próprios timers. 6 abas = 6x
// requisição pro servidor, sendo que todas perguntavam exatamente a mesma coisa.
// Em produção (gunicorn eventlet, 1 worker) isso pesa.
//
// Solução: uma aba pega um cadeado (navigator.locks) e vira a "dona". Só ela vai
// no servidor. O resultado volta pras outras por BroadcastChannel, que é memória
// do navegador — chega no mesmo instante, sem passar pela rede.
//
// Quem dita o ritmo continua sendo cada aba (aba escondida simplesmente não pede),
// e a dona só é responsável por buscar OU servir do cache. Assim:
//   - N abas visíveis pedindo a mesma coisa = 1 requisição por intervalo
//   - todas as abas escondidas = 0 requisição
//   - a dona morreu/fechou? o navegador solta o cadeado sozinho e outra assume
//
// Se o navegador não tiver BroadcastChannel/locks, tudo degrada pro comportamento
// antigo (cada aba busca a sua) — nada quebra.

export type PollSpec = {
  key: string;          // identidade do dado (ex.: "portal-nao-lidas:<flowId>")
  url: string;
  intervalMs: number;   // também é o TTL do cache: nunca busca mais rápido que isso
  method?: 'GET' | 'POST';
  body?: any;
};

type Msg =
  | { type: 'need'; spec: PollSpec; from: string }
  // `to` ausente = pra todo mundo (resultado de busca nova).
  // `to` preenchido = resposta de cache pra quem pediu, pra não fazer as
  // outras abas re-renderizarem à toa com um dado que elas já têm.
  | { type: 'data'; key: string; data: any; ts: number; to?: string };

const CHANNEL_NAME = 'escalone-shared-poll';
const LOCK_NAME = 'escalone-shared-poll-leader';

// Se a dona não responder nesse tempo, a aba busca por conta própria.
// Cobre: navegador sem suporte, dona travada, corrida na troca de dona.
const FALLBACK_MS = 4000;

const tabId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const hasChannel = typeof BroadcastChannel !== 'undefined';
const hasLocks =
  typeof navigator !== 'undefined' && !!(navigator as any).locks?.request;

const channel: BroadcastChannel | null = hasChannel
  ? new BroadcastChannel(CHANNEL_NAME)
  : null;

// Sem infra de coordenação, toda aba se vira sozinha (= comportamento antigo).
let isLeader = !(hasChannel && hasLocks);

const cache = new Map<string, { data: any; ts: number }>();
const inFlight = new Map<string, Promise<any>>();
const listeners = new Map<string, Set<(data: any) => void>>();
const fallbackTimers = new Map<string, ReturnType<typeof setTimeout>>();

// ---------------------------------------------------------------- eleição
//
// Enquanto a eleição não resolve não dá pra saber se sou a dona, e chutar sai
// caro: se eu chutar "não sou" sendo a única aba aberta, mando mensagem pra
// ninguém e só busco no fallback (4s de atraso na primeira pintura).
// Então os pedidos desse intervalo ficam na fila e saem quando resolver.
//
// Sou a primeira aba? o cadeado vem em ~1ms. Já tem outra aba dona? o cadeado
// nunca vem (fica na fila do navegador), e aí é o teto de tempo que resolve.
const ELECTION_TIMEOUT_MS = 300;

let electionSettled = !(hasChannel && hasLocks);
const pendingRequests: PollSpec[] = [];

function settleElection() {
  if (electionSettled) return;
  electionSettled = true;
  const fila = pendingRequests.splice(0);
  for (const spec of fila) dispatch(spec);
}

if (hasChannel && hasLocks) {
  (navigator as any).locks
    .request(LOCK_NAME, () => {
      isLeader = true;
      console.log('👑 [pollcompartilhado] esta aba virou a dona das requisições');
      settleElection();
      // Promise que nunca resolve: segura o cadeado enquanto a aba viver.
      // Quando ela fecha/trava, o navegador libera e a próxima da fila assume.
      return new Promise<never>(() => {});
    })
    .catch(() => {
      isLeader = false;
      settleElection();
    });

  setTimeout(settleElection, ELECTION_TIMEOUT_MS);
}

// ---------------------------------------------------------------- entrega
function emitLocal(key: string, data: any) {
  const timer = fallbackTimers.get(key);
  if (timer) {
    clearTimeout(timer);
    fallbackTimers.delete(key);
  }
  const set = listeners.get(key);
  if (!set) return;
  for (const cb of set) {
    try {
      cb(data);
    } catch (err) {
      console.error('[pollcompartilhado] erro ao aplicar dado de', key, err);
    }
  }
}

function publish(key: string, data: any, ts: number, to?: string) {
  if (!to) {
    // Dado novo: interessa a todas as abas.
    channel?.postMessage({ type: 'data', key, data, ts } as Msg);
    emitLocal(key, data);
    return;
  }
  if (to === tabId) {
    emitLocal(key, data);
    return;
  }
  channel?.postMessage({ type: 'data', key, data, ts, to } as Msg);
}

// ---------------------------------------------------------------- busca
function runFetch(spec: PollSpec): Promise<any> {
  const existing = inFlight.get(spec.key);
  if (existing) return existing; // já tem uma busca a caminho: aproveita ela

  const promise = (async () => {
    const init: RequestInit | undefined =
      spec.method === 'POST'
        ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(spec.body ?? {}),
          }
        : undefined;
    const res = await fetch(spec.url, init);
    const data = await res.json();
    cache.set(spec.key, { data, ts: Date.now() });
    return data;
  })();

  inFlight.set(spec.key, promise);
  promise.catch(() => {}).then(() => inFlight.delete(spec.key));
  return promise;
}

// Roda na dona: serve do cache se ainda estiver dentro do intervalo, senão busca.
// É isso que faz 6 abas pedindo virarem 1 requisição só.
async function serve(spec: PollSpec, para: string) {
  const cached = cache.get(spec.key);
  if (cached && Date.now() - cached.ts < spec.intervalMs) {
    publish(spec.key, cached.data, cached.ts, para);
    return;
  }
  // Já tem busca a caminho: ela publica pra todo mundo, inclusive pra quem
  // acabou de pedir. Sem isso, 3 abas pedindo davam 1 requisição mas 3
  // atualizações de tela.
  if (inFlight.has(spec.key)) return;
  try {
    const data = await runFetch(spec);
    publish(spec.key, data, Date.now());
  } catch (err) {
    // Silencioso de propósito: o próximo ciclo tenta de novo, igual ao
    // comportamento anterior (cada poll tinha seu try/catch e seguia em frente).
    console.warn('[pollcompartilhado] falha ao buscar', spec.key, err);
  }
}

function armFallback(spec: PollSpec) {
  if (fallbackTimers.has(spec.key)) return;
  const timer = setTimeout(async () => {
    fallbackTimers.delete(spec.key);
    if (isLeader) return; // virei dona nesse meio tempo
    try {
      const data = await runFetch(spec);
      publish(spec.key, data, Date.now());
    } catch {
      /* ignora: próximo ciclo tenta de novo */
    }
  }, FALLBACK_MS);
  fallbackTimers.set(spec.key, timer);
}

function dispatch(spec: PollSpec) {
  if (isLeader) {
    serve(spec, tabId);
    return;
  }
  channel?.postMessage({ type: 'need', spec, from: tabId } as Msg);
  armFallback(spec);
}

// ---------------------------------------------------------------- canal
channel?.addEventListener('message', (ev: MessageEvent) => {
  const msg = ev.data as Msg;
  if (!msg || typeof msg !== 'object') return;

  if (msg.type === 'need') {
    if (isLeader) serve(msg.spec, msg.from);
    return;
  }

  if (msg.type === 'data') {
    if (msg.to && msg.to !== tabId) return; // resposta de cache pra outra aba
    cache.set(msg.key, { data: msg.data, ts: msg.ts });
    emitLocal(msg.key, msg.data);
  }
});

// ---------------------------------------------------------------- API
export function subscribePoll(key: string, cb: (data: any) => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => {
    const current = listeners.get(key);
    if (!current) return;
    current.delete(cb);
    if (current.size === 0) listeners.delete(key);
  };
}

/** Pede o dado agora. Na dona busca/serve do cache; nas outras vai pelo canal. */
export function requestPoll(spec: PollSpec) {
  if (!electionSettled) {
    // Ainda não sei se sou a dona; guarda e sai assim que souber (~1ms a 300ms).
    if (!pendingRequests.some(p => p.key === spec.key)) pendingRequests.push(spec);
    return;
  }
  dispatch(spec);
}

/** Só pra diagnóstico no console. */
export function isPollLeader() {
  return isLeader;
}
